import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import StopIcon from '@mui/icons-material/Stop';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import './AICopilotToolbar.css';

/**
 * AI Copilot Toolbar for collaborative document editing
 * Provides quick access to AI editing commands and shows streaming status
 */
const AICopilotToolbar = ({
  isActive,
  isStreaming,
  onRequestEdit,
  onStopStreaming,
  onUndo,
  pendingChangesCount,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [quickActions] = useState([
    { label: 'Improve writing', command: 'Improve the writing quality and clarity of this document' },
    { label: 'Make it shorter', command: 'Make this document more concise while keeping key information' },
    { label: 'Fix grammar', command: 'Fix any grammar and spelling errors in this document' },
    { label: 'Add details', command: 'Expand this document with more details and examples' },
  ]);

  const handleSubmitInstruction = () => {
    if (instruction.trim() && onRequestEdit) {
      onRequestEdit(instruction);
      setInstruction('');
    }
  };

  const handleQuickAction = (command) => {
    if (onRequestEdit) {
      onRequestEdit(command);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitInstruction();
    }
  };

  return (
    <div className={`ai-copilot-toolbar ${isExpanded ? 'expanded' : ''}`}>
      <div className="toolbar-header">
        <div className="toolbar-title">
          <AutoFixHighIcon fontSize="small" />
          <span>AI Copilot</span>
          {isStreaming && (
            <span className="streaming-indicator">
              <span className="pulse-dot"></span>
              Editing...
            </span>
          )}
          {pendingChangesCount > 0 && !isStreaming && (
            <span className="pending-changes-badge">
              {pendingChangesCount} pending
            </span>
          )}
        </div>
        <div className="toolbar-actions">
          {isStreaming && (
            <Tooltip title="Stop streaming">
              <IconButton
                size="small"
                onClick={onStopStreaming}
                className="stop-button"
                color="error"
              >
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isStreaming && (
            <Tooltip title="Undo last change">
              <IconButton
                size="small"
                onClick={onUndo}
                disabled={disabled}
                className="undo-button"
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              className="expand-button"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {isExpanded && (
        <div className="toolbar-content">
          <div className="quick-actions">
            {quickActions.map((action, index) => (
              <Button
                key={index}
                variant="contained"
                size="small"
                onClick={() => handleQuickAction(action.command)}
                disabled={disabled || isActive}
                startIcon={<CheckCircleIcon fontSize="small" />}
                className="quick-action-button"
                sx={{
                  backgroundColor: 'white',
                  color: '#232323',
                  textTransform: 'none',
                  fontSize: '11px',
                  fontWeight: 700,
                  minHeight: '26px',
                  padding: '4px 10px',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: 'white',
                    opacity: 0.8,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                  },
                  '&:disabled': {
                    backgroundColor: 'white',
                    color: '#999',
                    opacity: 0.5,
                  }
                }}
              >
                {action.label}
              </Button>
            ))}
          </div>

          <Box className="custom-instruction">
            <TextField
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder="Describe what changes you want the AI to make..."
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled || isActive}
              className="instruction-input"
              variant="outlined"
            />
            <Button
              variant="contained"
              onClick={handleSubmitInstruction}
              disabled={disabled || isActive || !instruction.trim()}
              className="submit-button"
              startIcon={<AutoFixHighIcon />}
              sx={{
                backgroundColor: 'white',
                color: '#232323',
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: 700,
                minHeight: '32px',
                padding: '6px 14px',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: 'white',
                  opacity: 0.8,
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                },
                '&:disabled': {
                  backgroundColor: 'white',
                  color: '#999',
                  opacity: 0.5,
                }
              }}
            >
              Send
            </Button>
          </Box>
        </div>
      )}
    </div>
  );
};

AICopilotToolbar.propTypes = {
  isActive: PropTypes.bool,
  isStreaming: PropTypes.bool,
  onRequestEdit: PropTypes.func.isRequired,
  onStopStreaming: PropTypes.func,
  onUndo: PropTypes.func,
  pendingChangesCount: PropTypes.number,
  disabled: PropTypes.bool,
};

export default AICopilotToolbar;

